/**
 * LA CASCADA: orientar, ubicar la tarjeta, recortar, leer y consolidar.
 *
 * Esta es la secuencia, y nada más que la secuencia. Girar píxeles, correr el
 * OCR y decodificar un código son cosas que hacen los ADAPTADORES —canvas en el
 * navegador, sharp en node— porque dependen del entorno. El orden en que se
 * hacen y por qué, no.
 *
 * Ese corte es lo que permite ejercitar todo esto desde la línea de comandos con
 * fotos reales, que es donde aparecieron las fallas que a ojo no se ven.
 *
 * El orden no es arbitrario, cada paso salió de una medición:
 *
 *  1. ORIENTAR primero. Una foto vertical de un documento horizontal no da
 *     absolutamente nada: cero rótulos, cero campos. El cuarto de vuelta lo dice
 *     el código 2D por la posición de sus esquinas; si no hay código, la
 *     orientación en la que el texto más se parece a un MRZ.
 *  2. UBICAR Y RECORTAR. Una tarjeta que ocupa un tercio del cuadro no se lee
 *     por más que se amplíe la foto entera.
 *  3. LEER a la escala que corresponde, que sale de los px/mm de la tarjeta y
 *     del alto nominal del catálogo.
 *  4. CONSOLIDAR las fuentes sin que ninguna borre a otra.
 */
import { CATALOGO, Cara, ModeloDeDocumento, reconocerModelo } from "./catalogo";
import { CampoLeido, Rectangulo } from "./types";
import { Hallazgo, consolidar } from "./hallazgos";
import { filas, hallazgosDelFrente } from "./frente";
import { hallazgosDelDorso } from "./dorso";
import { hallazgosPorValorEsperado, ValorEsperado } from "./corroborar";
import { hallazgosDelCodigo } from "./pdf417";
import { anclaCreible, codigoTieneFormaEsperada, medirAncla, tarjetaPorSimilitud, unirDetecciones } from "./anclaje";
import { EsquinasDeTarjeta, cajaEnLaImagen, esquinasDeRectangulo } from "./redaccion";
import { cajaDelTramiteEntero, palabraDelTramite, recortarA } from "./tramite";
import { CaraDeLaFoto, caraPorSenales } from "./caraDeLaFoto";
import { pistaGirada } from "./marcoDeLaTarjeta";
import { inclinacionPorSimbolos, orientacionPorMrz } from "./anclajeMrz";
import { defectosDeLaLineaDeNombres, extractMrz as extraerMrz } from "../mrzTd1";
import {
  ALTO_DE_CARACTER_MM,
  ALTO_DE_CARACTER_OBJETIVO,
  AGRANDE_MAXIMO,
  ANCHO_OCR,
  DEMASIA_DEL_RECORTE,
  TARJETA_ID1,
} from "./tuning";
import { PalabraOcr } from "../dniMatch";
import { ImagenPlana } from "./quality";
import { tarjetaPorContorno } from "./contorno";

export interface ImagenConMedidas {
  ancho: number;
  alto: number;
}

/** Lo que el entorno tiene que saber hacer con una imagen. */
export interface AdaptadorDeImagen<T extends ImagenConMedidas> {
  girar(imagen: T, grados: number): Promise<T>;
  recortar(imagen: T, caja: Rectangulo): Promise<T>;
  escalarAAncho(imagen: T, ancho: number): Promise<T>;
  /** Para pasarle la imagen al decodificador de códigos. */
  aBlob(imagen: T): Promise<Blob>;
  /**
   * Los píxeles de la imagen, reducida si conviene (`factor` = píxeles de la
   * imagen por píxel devuelto). Opcional: sin esto no hay ubicación por contorno.
   */
  pixeles?(imagen: T): Promise<{ img: ImagenPlana; factor: number }>;
}

export interface LecturaOcr {
  texto: string;
  palabras: PalabraOcr[];
  simbolos: { bbox: { x0: number; y0: number; x1: number; y1: number } }[];
}

export interface AdaptadorDeOcr<T> {
  leer(imagen: T, whitelist?: string): Promise<LecturaOcr>;
  /**
   * Lectura con el modelo entrenado sobre el alfabeto del MRZ, si el entorno lo
   * tiene. Es opcional: sin él se lee con el común y se pierde calidad, no
   * funcionalidad.
   */
  leerMrz?(imagen: T): Promise<LecturaOcr>;
}

export interface CodigoLeido {
  texto: string;
  formato: string;
  esquinas: [{ x: number; y: number }, { x: number; y: number }, { x: number; y: number }, { x: number; y: number }] | null;
}

export interface AdaptadorDeCodigo {
  leer(blob: Blob): Promise<CodigoLeido[]>;
}

export interface Adaptadores<T extends ImagenConMedidas> {
  imagen: AdaptadorDeImagen<T>;
  ocr: AdaptadorDeOcr<T>;
  codigo?: AdaptadorDeCodigo;
}

export interface ResultadoDeLectura<T> {
  /** La imagen derecha y recortada. El original NUNCA se modifica. */
  imagen: T;
  /** Qué se le hizo al original para llegar a esa imagen. */
  transformacion: {
    giroGrados: number;
    recorte: Rectangulo | null;
    origenDelGiro: string;
    origenDeLaTarjeta: string;
  };
  campos: Record<string, CampoLeido>;
  hallazgos: Hallazgo[];
  /** El modelo reconocido, o null si ninguno llegó al mínimo. */
  modelo: ModeloDeDocumento | null;
  confianzaModelo: number;
  /** Inclinación remanente, en grados. null si no se pudo medir. */
  inclinacionGrados: number | null;
  tarjetaUbicada: boolean;
  /**
   * Las cuatro esquinas de la tarjeta en `imagen` (SI, SD, ID, II), o null si no
   * se ubicó. Pueden caer fuera de la imagen cuando la foto corta la tarjeta. Es
   * lo que usa el tapado por plantilla: las zonas del catálogo son fracciones de
   * la TARJETA, no de la imagen, que lleva un margen alrededor.
   */
  tarjetaEnLaImagen: EsquinasDeTarjeta | null;
  /**
   * Si la tarjeta salió de un ANCLA del documento (el código 2D, las líneas del
   * MRZ) y no de su contorno. Solo con ancla se tapa por plantilla: el contorno
   * sirve para recortar y leer, pero sus esquinas (redondeadas, a veces con
   * sombra) no tienen la precisión que pide tapar a medio milímetro del número
   * de documento. Y solo con un ancla que el modelo declara en esta cara: el
   * frente 2012 se ancla por su PDF417, nunca por un MRZ, que no lleva.
   */
  tarjetaPorAncla: boolean;
  /**
   * Qué cara trae la foto según lo que se vio en ella (caraDeLaFoto), sin importar
   * en qué paso llegó. null si no hay señales.
   */
  caraReconocida: CaraDeLaFoto | null;
  /**
   * El Nº de Trámite impreso, en `imagen`, cuando el OCR de su banda encontró el
   * número que dio el código (ver tramite.ts). null si no.
   */
  tramiteEnLaImagen: Rectangulo | null;
  pxPorMm: number | null;
  /**
   * Los renglones que vio el OCR sobre el impreso, en orden.
   *
   * Un campo que falta es indistinguible de un campo que no estaba: sin esto no
   * hay forma de saber si el problema fue la foto, el recorte o el rótulo. Es
   * lo mismo que ya se muestra del dorso cuando algo no se pudo leer.
   */
  renglonesDelImpreso: string[];
  /**
   * Zonas que hay que TAPAR aunque no se haya reconocido el modelo, en
   * coordenadas de `imagen`.
   *
   * Hoy es una sola: el código 2D del que se sacó un número de trámite. No es
   * una suposición de plantilla —"acá suele haber un código"—: es el símbolo
   * que ACABAMOS DE DECODIFICAR y del que leímos el número. Tapar el número
   * impreso y dejar el código que lo contiene es no taparlo.
   */
  zonasSensibles: Rectangulo[];
  diagnostico: string[];
}

const MRZ_WHITELIST = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789<";

/** Prueba de decodificación sobre una zona de la imagen. */
async function intentarCodigo<T extends ImagenConMedidas>(
  imagen: T,
  ad: Adaptadores<T>
): Promise<CodigoLeido | null> {
  if (!ad.codigo) return null;
  const { INTENTOS_DE_DECODIFICACION } = await import("./pdf417");
  for (const intento of INTENTOS_DE_DECODIFICACION) {
    const { x, y, w, h } = intento.zona;
    let recortada = imagen;
    if (w < 1 || h < 1) {
      recortada = await ad.imagen.recortar(imagen, {
        x: imagen.ancho * x,
        y: imagen.alto * y,
        w: imagen.ancho * w,
        h: imagen.alto * h,
      });
    }
    const ancho = Math.round(recortada.ancho * intento.escala);
    const escalada = ancho === recortada.ancho ? recortada : await ad.imagen.escalarAAncho(recortada, ancho);
    const encontrados = await ad.codigo.leer(await ad.imagen.aBlob(escalada));
    if (encontrados.length === 0 || !encontrados[0].esquinas) continue;
    // El mismo símbolo puede volver partido en varias detecciones, cada una con
    // parte de las filas: se unen (ver unirDetecciones).
    const delMismo = encontrados
      .filter((e) => e.esquinas && e.texto === encontrados[0].texto)
      .map((e) => e.esquinas!);
    const unido = unirDetecciones(delMismo) ?? encontrados[0].esquinas;
    // Las esquinas vuelven a coordenadas de la imagen que entró.
    const f = escalada.ancho / recortada.ancho;
    const esquinas = unido.map((p) => ({
      x: p.x / f + imagen.ancho * x,
      y: p.y / f + imagen.alto * y,
    })) as CodigoLeido["esquinas"];
    return { ...encontrados[0], esquinas };
  }
  return null;
}

function cuartoDeVueltaDe(esquinas: NonNullable<CodigoLeido["esquinas"]>): number {
  const [si, sd] = esquinas;
  const ang = (Math.atan2(sd.y - si.y, sd.x - si.x) * 180) / Math.PI;
  return (Math.round((((ang % 360) + 360) % 360) / 90) * 90) % 360;
}

/**
 * Lee un documento: orienta, identifica el modelo, encuadra, lee y consolida.
 *
 * El MODELO se reconoce acá adentro y no se recibe. Reconocerlo afuera era
 * hacerlo con menos información: las señales que más pesan —si el código 2D
 * decodificó y en qué cara, si el MRZ parseó— solo existen después de haber
 * mirado la imagen, y son justamente las que distinguen un formato de otro.
 *
 * Si no se reconoce ninguno, se lee igual pero SIN plantilla: el MRZ y el
 * código no dependen del modelo. Lo que no se hace es suponer uno, porque
 * aplicar la plantilla equivocada tapa lo que no corresponde.
 */
/**
 * A qué ancho leer una imagen de la que NO se sabe la escala.
 *
 * Cuando la tarjeta se ubica, la escala sale de su geometría. Cuando no, lo
 * único que se puede suponer es que la foto es más o menos la tarjeta, y ahí el
 * ancho que pone el carácter en su tamaño útil es una constante: 20 px por cada
 * 1,3 mm sobre 85,6 mm de tarjeta.
 *
 * Antes esto era `min(ancho, ANCHO_OCR)`, que ACHICA lo grande y no agranda lo
 * chico. Una foto de 545 px de ancho tiene caracteres de 8 px y no se lee nada;
 * medido sobre un dorso real, agrandándola aparecen rótulos donde no había
 * ninguno. Lo grande se sigue tratando igual que siempre.
 */
function anchoDeTrabajo(ancho: number): number {
  const objetivo = Math.round((ALTO_DE_CARACTER_OBJETIVO * TARJETA_ID1.anchoMm) / ALTO_DE_CARACTER_MM);
  // Se agranda SOLO lo que no tiene ninguna chance al tamaño que vino: menos de
  // la mitad del útil, o sea un carácter de menos de 10 px. Agrandar una foto
  // que ya andaba hace daño, y está medido: el frente de 2010 entra a 960 px
  // —carácter de 15— y llevándolo al objetivo se perdía el MRZ entero, con él
  // la ubicación de la tarjeta y con ella los siete campos probados.
  if (ancho >= objetivo / 2) return Math.min(ancho, ANCHO_OCR);
  return Math.min(objetivo, ancho * AGRANDE_MAXIMO);
}

/**
 * Los campos de un MRZ como hallazgos. Un campo cuyo dígito de control NO cierra
 * está mal leído y se descarta; los que no tienen verificador (apellido, nombres,
 * sexo, nacionalidad) van sin probar. Lo usa la cascada y también el hook, para el
 * MRZ que lee aparte (con el auxilio de 180°).
 */
export function hallazgosDelMrz(mrz: ReturnType<typeof extraerMrz>): Hallazgo[] {
  return mrz
    ? ([
        ["apellido", mrz.surname, null, true],
        ["nombres", mrz.givenNames, null, true],
        ["numeroDocumento", mrz.documentNumber, mrz.checks.doc, false],
        ["fechaNacimiento", mrz.birthDate, mrz.checks.birth, false],
        ["fechaVencimiento", mrz.expiryDate, mrz.checks.expiry, false],
        ["sexo", mrz.sex, null, false],
        // La nacionalidad del MRZ es un código de tres letras y la tarjeta
        // imprime el país entero: "ARG" y "ARGENTINA" son el mismo dato, y el
        // corto no tiene que competir con el largo. Es la misma situación que
        // el nombre cortado a los 30 caracteres.
        ["nacionalidad", mrz.nationality, null, true],
      ] as [string, string | null, boolean | null, boolean][])
        .filter(([, v]) => !!v)
        // `null` = ese campo no tiene verificador; `false` = lo tiene y falló.
        .filter(([, , check]) => check !== false)
        .map(([campo, valor, check, truncable]) => ({
          campo,
          valor: String(valor),
          crudo: String(valor),
          origen: "mrz" as const,
          probado: check === true,
          puedeEstarTruncado: truncable,
        }))
    : [];
}

/** Una foto es apaisada si es al menos esto más ancha que alta. */
const APAISADA = 1.15;

export interface MedidaDeGiro {
  giro: number;
  puntaje: number;
  mrzParseado: boolean;
}

/**
 * El giro a aplicar, por lo que midió el OCR en cada una de las cuatro.
 *
 * Gana el puntaje más alto, con una salvedad medida sobre el corpus: en una foto
 * APAISADA, girarla un cuarto de vuelta la deja vertical, y una tarjeta es
 * apaisada. Ahí un cuarto de vuelta necesita un MRZ que parsee o dos campos más
 * que la foto como vino: un frente 2012 derecho, de 1024 px, se giraba 90° porque
 * el texto acostado dio un «campo» de ruido más que el derecho, y con eso se leía
 * un apellido de basura que contradecía al del dorso.
 */
export function elegirGiro(medidas: MedidaDeGiro[], apaisada: boolean): number {
  const derecho = medidas.find((m) => m.giro === 0)?.puntaje ?? 0;
  let mejor = { giro: 0, puntaje: 0 };
  for (const m of medidas) {
    const cuarto = m.giro === 90 || m.giro === 270;
    if (apaisada && cuarto && !m.mrzParseado && m.puntaje < derecho + 0.45) continue;
    if (m.puntaje > mejor.puntaje) mejor = { giro: m.giro, puntaje: m.puntaje };
  }
  return mejor.giro;
}

export async function leerDocumento<T extends ImagenConMedidas>(
  original: T,
  cara: Cara,
  ad: Adaptadores<T>,
  catalogo: ModeloDeDocumento[] = CATALOGO,
  /**
   * Lo que YA se sabe del documento por la otra cara.
   *
   * La tarjeta tiene una sola identidad repartida en dos caras: el MRZ del
   * dorso prueba el nombre que está impreso en el frente. Sin esto, cada cara
   * se lee sola y el frente de un documento cuyos rótulos no se leen no puede
   * señalar nada, aunque el dorso ya lo haya probado todo.
   */
  esperadosDeLaOtraCara: ValorEsperado[] = [],
  /**
   * El marco de la cámara, en fracciones de la foto que entró: una pista de dónde
   * buscar el contorno de la tarjeta cuando no hay ancla (marcoDeLaTarjeta). Nunca
   * da garantía ni recorta por sí mismo: sin contorno, la foto sigue entera.
   */
  opciones: { pistaDeTarjeta?: Rectangulo } = {}
): Promise<ResultadoDeLectura<T>> {
  const diagnostico: string[] = [];

  // --- 1. Cuarto de vuelta -------------------------------------------------
  let codigo = await intentarCodigo(original, ad);
  let contenidoA = 0;
  let origenDelGiro = "ninguno";

  if (codigo?.esquinas) {
    contenidoA = cuartoDeVueltaDe(codigo.esquinas);
    origenDelGiro = `código ${codigo.formato}`;
  } else {
    // El puntaje depende de la CARA. Puntuar un frente por densidad de MRZ no
    // tiene sentido —el frente no tiene MRZ— y el ruido de cualquier
    // orientación gana: medido, daba 1.00 a 90° sobre un frente y lo dejaba de
    // costado, sin leer nada.
    // El puntaje del frente son los HALLAZGOS, no los rótulos reconocidos.
    //
    // Contar rótulos es fácil de engañar: la coincidencia es difusa —tiene que
    // serlo, el OCR se come letras— y un texto basura acumula coincidencias por
    // casualidad. Un hallazgo, en cambio, pasó además el control de forma: una
    // fecha que existe, un documento de siete u ocho dígitos, una M o una F
    // sola. Sacar eso de ruido es mucho menos probable.
    //
    // Se registra el puntaje de las cuatro para poder ver, cuando algo sale
    // torcido, qué vio el OCR en cada una.
    const puntajes: string[] = [];
    const medidas: MedidaDeGiro[] = [];
    for (const giro of [0, 90, 180, 270]) {
      const girada = giro === 0 ? original : await ad.imagen.girar(original, giro);
      const ancho = anchoDeTrabajo(girada.ancho);
      const paraOcr = ancho === girada.ancho ? girada : await ad.imagen.escalarAAncho(girada, ancho);
      // Se puntúa por las DOS señales, sin importar la cara, y gana la mejor.
      //
      // Atar el MRZ al dorso era meter un supuesto de formato en el motor: en
      // el diseño 2009-2012 el MRZ está en el FRENTE. Medido sobre uno real, el
      // lector por rótulos saca CERO en las cuatro orientaciones —sus etiquetas
      // están impresas en una tipografía chica que el OCR no ve— y el MRZ de su
      // frente parsea con los cuatro dígitos de control en true.
      const { texto } = await ad.ocr.leer(paraOcr, MRZ_WHITELIST);
      const { palabras } = await ad.ocr.leer(paraOcr);

      // Lo que decide es que el MRZ PARSEE y cuántos de sus dígitos de control
      // cierran. La densidad no sirve para elegir: satura en 1,00 y empataba en
      // tres orientaciones de la misma foto. Un verificador que cierra es una
      // prueba, y cerrarlos todos en la orientación equivocada es imposible.
      const posible = extraerMrz(texto);
      const cierran = posible
        ? [posible.checks.doc, posible.checks.birth, posible.checks.expiry, posible.checks.composite]
            .filter((c) => c === true).length
        : 0;
      const porMrz = posible ? 0.6 + 0.1 * cierran : 0;
      const porRotulos = Math.min(0.9, hallazgosDelFrente(palabras).length * 0.225);
      const puntaje = Math.max(porMrz, porRotulos);
      puntajes.push(
        `${giro}°:${puntaje.toFixed(2)}(mrz ${posible ? cierran + "/4" : "no"}, campos ${hallazgosDelFrente(palabras).length})`
      );
      medidas.push({ giro, puntaje, mrzParseado: !!posible });
    }
    const mejor = { giro: elegirGiro(medidas, original.ancho >= original.alto * APAISADA) };
    // Acá `giro` es el que hay que APLICAR, así que el contenido está al revés.
    contenidoA = (360 - mejor.giro) % 360;
    origenDelGiro = puntajes.join(" ");
  }

  // Con el contenido a +90°, la imagen se gira -90. Girar en el mismo sentido
  // la deja cabeza abajo, que fue exactamente lo que pasó la primera vez.
  const giroGrados = (360 - contenidoA) % 360;
  diagnostico.push(`contenido a ${contenidoA}° → giro ${giroGrados}° [${origenDelGiro}]`);

  let trabajo = giroGrados === 0 ? original : await ad.imagen.girar(original, giroGrados);
  if (giroGrados !== 0 && codigo) codigo = await intentarCodigo(trabajo, ad);

  // --- 2. Identificar el modelo -------------------------------------------
  // Con todo lo visto: los rótulos fijos, qué código apareció y en qué cara, y
  // si el MRZ parseó. Es el momento en que se tiene más información.
  const anchoIdent = anchoDeTrabajo(trabajo.ancho);
  const paraIdent =
    anchoIdent === trabajo.ancho ? trabajo : await ad.imagen.escalarAAncho(trabajo, anchoIdent);
  const vistaIdent = await ad.ocr.leer(paraIdent);
  const franjaIdent = await ad.imagen.recortar(trabajo, {
    x: 0, y: trabajo.alto * 0.55, w: trabajo.ancho, h: trabajo.alto * 0.45,
  });
  const mrzIdent = extraerMrz((await ad.ocr.leer(franjaIdent, MRZ_WHITELIST)).texto);

  const identificacion = reconocerModelo(
    {
      texto: vistaIdent.texto,
      palabras: vistaIdent.palabras,
      codigos2d: codigo?.texto
        ? [{ cara, tipo: codigo.formato.toLowerCase().includes("qr") ? "qr" : "pdf417", contenido: codigo.texto }]
        : [],
      mrz: mrzIdent ? "TD1" : null,
      cara,
    },
    catalogo
  );
  // El código 2D dice el modelo mejor que los rótulos: si decodificó como el
  // código de un DNI, con la forma que tiene en el catálogo, y un solo modelo lo
  // declara en esta cara, es ese. Medido sobre el corpus: frentes 2012 con el
  // PDF417 leído quedaban sin modelo porque el OCR no leía la mitad de los
  // rótulos, y sin modelo no había plantilla.
  const tipoDelCodigo = codigo?.texto ? (codigo.formato.toLowerCase().includes("qr") ? "qr" : "pdf417") : null;
  /** Si el modelo declara este código en esta cara, con la forma de su contenido. */
  const declaraElCodigo = (m: ModeloDeDocumento) =>
    !!codigo?.texto &&
    m.codigos2d.some(
      (c) => c.cara === cara && c.tipo === tipoDelCodigo && (!c.forma || c.forma.test(codigo.texto.trim()))
    );
  const declaranElCodigo = tipoDelCodigo ? catalogo.filter(declaraElCodigo) : [];
  const codigoDeUnDni =
    !!codigo?.esquinas &&
    hallazgosDelCodigo(codigo.texto).length > 0 &&
    (tipoDelCodigo !== "pdf417" || codigoTieneFormaEsperada({ esquinas: codigo.esquinas }));
  const modeloPorCodigo = codigoDeUnDni && declaranElCodigo.length === 1 ? declaranElCodigo[0] : null;
  if (modeloPorCodigo && modeloPorCodigo !== identificacion.modelo) {
    diagnostico.push(`modelo ${modeloPorCodigo.id} por su código (los rótulos daban ${identificacion.modelo?.id ?? "ninguno"})`);
  }
  const modelo = modeloPorCodigo ?? identificacion.modelo;
  diagnostico.push(
    modelo
      ? `modelo ${modelo.id} (${identificacion.confianza.toFixed(2)})`
      : `modelo no reconocido (mejor ${identificacion.confianza.toFixed(2)}): se lee sin plantilla`
  );

  // --- 3. La tarjeta -------------------------------------------------------
  // Sus cuatro esquinas en `trabajo`, y la caja que las contiene.
  let esquinasTarjeta: EsquinasDeTarjeta | null = null;
  let tarjeta: Rectangulo | null = null;
  let pxPorMm: number | null = null;
  let origenDeLaTarjeta = "ninguno";

  // Por las cuatro esquinas del código, con la caja que el catálogo le da en la
  // tarjeta: una homografía, que no depende del fondo ni de que la tarjeta entre
  // entera en la foto (M1). Solo con un código de la forma esperada: un PDF417
  // con otra forma no es el del frente 2012 —el diseño 2009 lleva uno grande en
  // el dorso— y anclar ahí plantaría la tarjeta en otro lado.
  const zonaDelCodigo = modelo?.zonas.find((z) => z.cara === cara && z.campo === tipoDelCodigo && z.tipo === "codigo2d");
  // El código tiene que ser el que el modelo declara, con la forma de su contenido, venga el
  // modelo del código o de los rótulos: el frente 2009 comparte rótulos con el 2012 y, en una
  // foto con las dos caras sin separar, el PDF417 del dorso 2009 anclaba una tarjeta falsa.
  if (zonaDelCodigo && codigoDeUnDni && modelo && declaraElCodigo(modelo) && codigo?.esquinas) {
    // Con control (revisión, importante 4): la proporción del código
    // contra la plantilla, y la tarjeta mayormente dentro de la foto. Un código mal leído no ancla:
    // ni tapado ni recorte por esa tarjeta. La tarjeta sale de una similitud por cuadrados mínimos,
    // que reparte el error de una esquina en vez de proyectarlo hasta la esquina opuesta.
    const medidas = medirAncla(codigo.esquinas, zonaDelCodigo.caja, trabajo);
    if (anclaCreible(medidas)) {
      esquinasTarjeta = tarjetaPorSimilitud(codigo.esquinas, zonaDelCodigo.caja);
      if (esquinasTarjeta) origenDeLaTarjeta = modeloPorCodigo ? "código 2D" : `código 2D (${modelo!.id} por sus rótulos)`;
    } else {
      diagnostico.push(
        medidas
          ? `el código no ancla: proporción ${medidas.proporcion.toFixed(2)}, afuera ${(medidas.afuera * 100).toFixed(0)} %`
          : "el código no ancla"
      );
    }
  }
  if (!esquinasTarjeta) {
    const ancho = anchoDeTrabajo(trabajo.ancho);
    const paraOcr = ancho === trabajo.ancho ? trabajo : await ad.imagen.escalarAAncho(trabajo, ancho);
    const { palabras } = await ad.ocr.leer(paraOcr);
    const o = orientacionPorMrz(palabras);
    if (o?.tarjeta) {
      const f = trabajo.ancho / paraOcr.ancho;
      esquinasTarjeta = esquinasDeRectangulo({ x: o.tarjeta.x * f, y: o.tarjeta.y * f, w: o.tarjeta.w * f, h: o.tarjeta.h * f });
      origenDeLaTarjeta = "renglones del MRZ";
    }
  }
  // El ancla habilita la plantilla solo si el modelo la declara en esta cara: el MRZ de un frente
  // ubica bien la tarjeta, pero si el modelo reconocido no lleva MRZ en el frente (el 2012), el
  // modelo está equivocado —es el frente 2009, que comparte sus rótulos— y su plantilla taparía
  // en otro lugar. La tarjeta igual sirve para recortar y leer.
  const mrzQueElModeloNoDeclara =
    origenDeLaTarjeta === "renglones del MRZ" && !!modelo && !modelo.zonas.some((z) => z.tipo === "mrz" && z.cara === cara);
  if (mrzQueElModeloNoDeclara) {
    diagnostico.push(`el ${modelo!.id} no lleva MRZ en el ${cara}: el MRZ ubica la tarjeta, pero no habilita su plantilla`);
  }
  const tarjetaPorAncla = !!esquinasTarjeta && !mrzQueElModeloNoDeclara;
  // Sin ancla y con el marco de la cámara: el contorno se busca dentro del marco (con un margen).
  // El marco nunca es la tarjeta: si el DNI quedó más grande o corrido, recortar al marco le cortaba
  // partes a la foto que se entrega. Sin contorno, se sigue como sin marco. Nada de esto ancla.
  if (!esquinasTarjeta && opciones.pistaDeTarjeta) {
    const p = pistaGirada(opciones.pistaDeTarjeta, giroGrados);
    const margen = 0.1;
    const zx = Math.max(0, (p.x - p.w * margen) * trabajo.ancho);
    const zy = Math.max(0, (p.y - p.h * margen) * trabajo.alto);
    const zw = Math.min(trabajo.ancho - zx, p.w * (1 + 2 * margen) * trabajo.ancho);
    const zh = Math.min(trabajo.alto - zy, p.h * (1 + 2 * margen) * trabajo.alto);
    if (ad.imagen.pixeles && zw > 50 && zh > 50) {
      const zona = await ad.imagen.recortar(trabajo, { x: zx, y: zy, w: zw, h: zh });
      const { img, factor } = await ad.imagen.pixeles(zona);
      const q = tarjetaPorContorno(img);
      if (q) {
        esquinasTarjeta = q.map((pt) => ({ x: Math.round(zx) + pt.x * factor, y: Math.round(zy) + pt.y * factor })) as EsquinasDeTarjeta;
        origenDeLaTarjeta = "contorno dentro del marco";
      }
    }
  }
  // Sin ancla, el contorno (M2): bordes y un cuadrilátero de proporción ID-1.
  if (!esquinasTarjeta && ad.imagen.pixeles) {
    const { img, factor } = await ad.imagen.pixeles(trabajo);
    const q = tarjetaPorContorno(img);
    if (q) {
      esquinasTarjeta = q.map((p) => ({ x: p.x * factor, y: p.y * factor })) as EsquinasDeTarjeta;
      origenDeLaTarjeta = "contorno";
    }
  }
  if (esquinasTarjeta) {
    const xs = esquinasTarjeta.map((p) => p.x);
    const ys = esquinasTarjeta.map((p) => p.y);
    tarjeta = { x: Math.min(...xs), y: Math.min(...ys), w: Math.max(...xs) - Math.min(...xs), h: Math.max(...ys) - Math.min(...ys) };
    const [si, sd] = esquinasTarjeta;
    pxPorMm = Math.hypot(sd.x - si.x, sd.y - si.y) / TARJETA_ID1.anchoMm;
  }
  diagnostico.push(
    tarjeta
      ? `tarjeta ${Math.round(tarjeta.w)}x${Math.round(tarjeta.h)} [${origenDeLaTarjeta}]`
      : "no se pudo ubicar la tarjeta: se lee la foto como vino"
  );

  // --- 4. Recorte ----------------------------------------------------------
  let recorte: Rectangulo | null = null;
  if (tarjeta) {
    // Ante la duda que sobre: las anclas son estimaciones y un renglón cortado
    // se pierde, mientras que unos milímetros de mesa no molestan a nadie.
    const margen = tarjeta.w * DEMASIA_DEL_RECORTE;
    const x = Math.max(0, Math.round(tarjeta.x - margen));
    const y = Math.max(0, Math.round(tarjeta.y - margen));
    const w = Math.min(trabajo.ancho - x, Math.round(tarjeta.w + 2 * margen));
    const h = Math.min(trabajo.alto - y, Math.round(tarjeta.h + 2 * margen));
    if (w > 50 && h > 50) {
      recorte = { x, y, w, h };
      trabajo = await ad.imagen.recortar(trabajo, recorte);
      pxPorMm = trabajo.ancho / TARJETA_ID1.anchoMm;
    }
  }
  // Las esquinas, en la imagen que sale.
  const tarjetaEnLaImagen: EsquinasDeTarjeta | null = esquinasTarjeta
    ? (esquinasTarjeta.map((p) => ({ x: p.x - (recorte?.x ?? 0), y: p.y - (recorte?.y ?? 0) })) as EsquinasDeTarjeta)
    : null;

  // --- 5. Leer -------------------------------------------------------------
  // La escala sale de la geometría: se lleva el CARÁCTER a su tamaño útil, en
  // vez de llevar la imagen a un ancho fijo. Medido: el mismo recorte lee 2
  // campos con caracteres de 12 px, 4 con 19 px, y vuelve a 2 con 28 px.
  const anchoParaOcr = pxPorMm
    ? Math.round(trabajo.ancho * (ALTO_DE_CARACTER_OBJETIVO / (pxPorMm * ALTO_DE_CARACTER_MM)))
    : anchoDeTrabajo(trabajo.ancho);
  const paraOcr =
    anchoParaOcr === trabajo.ancho ? trabajo : await ad.imagen.escalarAAncho(trabajo, Math.max(200, anchoParaOcr));
  diagnostico.push(`OCR a ${paraOcr.ancho}px${pxPorMm ? ` (${(pxPorMm * ALTO_DE_CARACTER_MM).toFixed(1)} px por carácter antes de escalar)` : ""}`);

  const impreso = await ad.ocr.leer(paraOcr);
  const escalaOcr = paraOcr.ancho / trabajo.ancho;
  // Cada cara con SU tabla de rótulos y SU regla geométrica: en el frente el
  // valor está debajo del rótulo porque el frente es una grilla; en el dorso
  // está al lado, en el mismo renglón.
  //
  // Usar la del frente en el dorso no era inofensivo: "PAIS DE NACIMIENTO"
  // enganchaba con el rótulo de la fecha de nacimiento y "FECHA DE RADICACION
  // ... VENCIMIENTO" con el del vencimiento del documento, que es otra cosa.
  const leerImpreso = cara === "dorso" ? hallazgosDelDorso : hallazgosDelFrente;
  const hImpreso = leerImpreso(impreso.palabras).map((h) => ({
    ...h,
    caja: h.caja
      ? { x: h.caja.x / escalaOcr, y: h.caja.y / escalaOcr, w: h.caja.w / escalaOcr, h: h.caja.h / escalaOcr }
      : undefined,
  }));

  const altoFranja = Math.round(trabajo.alto * 0.45);
  const franja = await ad.imagen.recortar(trabajo, {
    x: 0, y: trabajo.alto - altoFranja, w: trabajo.ancho, h: altoFranja,
  });
  // Se lee con los dos modelos y gana el que cierre MÁS dígitos de control.
  //
  // Ninguno domina siempre, medido: sobre un dorso el modelo común cierra 4 de
  // 4 y el del MRZ 3; sobre otro el común cierra 2 y el del MRZ 4, y además
  // devuelve los rellenos y los nombres limpios. Elegir por los verificadores
  // es elegir por una prueba, que es la única forma de decidir sin adivinar.
  const cuantosCierran = (d: ReturnType<typeof extraerMrz>) =>
    d ? [d.checks.doc, d.checks.birth, d.checks.expiry, d.checks.composite].filter((c) => c === true).length : -1;

  const lecturas = [await ad.ocr.leer(franja, MRZ_WHITELIST)];
  if (ad.ocr.leerMrz) {
    try {
      lecturas.push(await ad.ocr.leerMrz(franja));
    } catch {
      // Sin el modelo especializado se sigue con el común.
    }
  }
  // Cuando los dos cierran los MISMOS verificadores hay que desempatar por otro
  // lado, porque la línea de nombres no tiene dígito de control ni entra en el
  // compuesto: ahí decide la forma que el propio TD1 le exige a esa línea.
  // Medido sobre el frente de 2010: con el empate en 4/4, el modelo común daba
  // "RODOLFOSMANUEL LK" y el del MRZ "RODOLFO MANUEL".
  const defectos = (d: ReturnType<typeof extraerMrz>) =>
    d ? defectosDeLaLineaDeNombres(d.raw3) : Number.MAX_SAFE_INTEGER;

  const candidatas = lecturas.map((l) => ({ lectura: l, mrz: extraerMrz(l.texto) }));
  const elegida = candidatas.reduce((mejor, c) => {
    const dif = cuantosCierran(c.mrz) - cuantosCierran(mejor.mrz);
    if (dif !== 0) return dif > 0 ? c : mejor;
    return defectos(c.mrz) < defectos(mejor.mrz) ? c : mejor;
  });
  if (candidatas.length > 1) {
    diagnostico.push(
      `MRZ: ${candidatas
        .map((c) => (c.mrz ? `${cuantosCierran(c.mrz)}/4 (forma ${defectos(c.mrz)})` : "no"))
        .join(" vs ")}`
    );
  }
  const lecturaMrz = elegida.lectura;
  const inclinacionGrados = inclinacionPorSimbolos(lecturaMrz.simbolos);

  const mrz = elegida.mrz;
  // Un campo del MRZ cuyo dígito de control NO cierra está MAL LEÍDO, y se
  // descarta. No es una lectura con menos confianza: el verificador es una
  // prueba, y fallarlo prueba que el número no es ese.
  //
  // Marcarlos todos como probados sin mirar los checks fue un error con
  // consecuencia visible: con la escala equivocada el MRZ devuelve números
  // basura —medido: 1845220 y 18475220 donde dice 31845220— y se mostraban
  // como verificados.
  //
  // Qué cubre cada verificador del TD1, y qué NO cubre nadie:
  //
  //   dígito del documento → número
  //   dígito de nacimiento → fecha de nacimiento
  //   dígito de vencimiento → fecha de vencimiento
  //   compuesto → línea 1 [5..30) y línea 2 [0..7) [8..15) [18..29)
  //
  // El compuesto NO alcanza la línea 3: el apellido y los nombres no los
  // verifica NINGÚN dígito, y el sexo y la nacionalidad tampoco (quedan fuera
  // de los tramos del compuesto). Todos esos van como lectura única, y se
  // vuelven `coincidencia_multiple` solo si el código 2D dice lo mismo.
  //
  // Decir lo contrario no era un matiz: sobre el frente de 2010 el MRZ cerraba
  // los 4 dígitos y devolvía el apellido "LOVIRA" —una G leída como L—, y se
  // mostraba como verificado por dígito de control.
  const hMrz: Hallazgo[] = hallazgosDelMrz(mrz);

  const hCodigo = codigo?.texto ? hallazgosDelCodigo(codigo.texto) : [];

  // Lo que una fuente probada ya dijo, buscado adentro del impreso.
  //
  // Leer el frente a ciegas necesita que el rótulo se lea, y hay documentos
  // donde no se lee —los de 2009-2012 los imprimen chicos y espaciados—. Saber
  // de antemano que el apellido es ROVIRA convierte eso en un problema fácil, y
  // da las dos cosas que faltaban: la CAJA para señalar el dato en la foto, y
  // una segunda lectura para los campos que ningún dígito de control cubre.
  //
  // El número de trámite queda afuera a propósito: es el dato que se tapa, no
  // uno que se señale.
  const yaLeidos = new Set(hImpreso.map((h) => h.campo));
  const deEstaCara = [...hCodigo, ...hMrz]
    // Cualquier campo que haya salido de una fuente legible por máquina sirve
    // como expectativa. Pedirle `probado` dejaba afuera justo los cuatro que
    // ningún dígito cubre —apellido, nombres, sexo y nacionalidad—, que son los
    // que más necesitan una segunda fuente.
    .filter((h) => h.origen === "mrz" || h.origen === "pdf417" || h.origen === "qr")
    .filter((h) => h.campo !== "numeroTramite" && !yaLeidos.has(h.campo))
    .map((h) => ({ campo: h.campo, valor: h.valor }));
  // Lo de esta cara primero: si las dos caras dicen algo del mismo campo, se
  // busca lo que probó la fuente que está acá.
  const vistos = new Set(deEstaCara.map((e) => e.campo));
  const esperados = [
    ...deEstaCara,
    ...esperadosDeLaOtraCara.filter(
      (e) => e.valor && e.campo !== "numeroTramite" && !vistos.has(e.campo) && !yaLeidos.has(e.campo)
    ),
  ];
  const hCorroborado = hallazgosPorValorEsperado(impreso.palabras, esperados).map((h) => ({
    ...h,
    caja: h.caja
      ? { x: h.caja.x / escalaOcr, y: h.caja.y / escalaOcr, w: h.caja.w / escalaOcr, h: h.caja.h / escalaOcr }
      : undefined,
  }));

  // El número de documento impreso, buscado en SU ZONA con el valor que ya probó
  // otra fuente. Recorrido 2: en una foto con las dos caras, el frente separado
  // (620 px de tarjeta) daba el número por el código, pero el OCR de toda la cara
  // lo devolvía en pedazos de tres y cuatro dígitos —con letras grandes y
  // espaciadas, partido por los puntos de miles— y no había caja para marcarlo.
  // Leer solo esa zona, ampliada y con dígitos y puntos, lo encuentra.
  const hNumero: Hallazgo[] = [];
  const yaUbicado = [...hImpreso, ...hCorroborado].some((h) => h.campo === "numeroDocumento" && h.caja);
  const numeroEsperado =
    [...hCodigo, ...hMrz].find((h) => h.campo === "numeroDocumento" && h.probado)?.valor ??
    esperadosDeLaOtraCara.find((e) => e.campo === "numeroDocumento")?.valor;
  const zonaNumero = modelo?.zonas.find((z) => z.cara === cara && z.campo === "numeroDocumento");
  if (!yaUbicado && numeroEsperado && zonaNumero && tarjetaEnLaImagen && tarjetaPorAncla) {
    const z = zonaNumero.caja;
    const banda = cajaEnLaImagen({ x: z.x - 0.03, y: z.y - 0.03, w: z.w + 0.06, h: z.h + 0.05 }, tarjetaEnLaImagen);
    if (banda) {
      const x = Math.max(0, banda.x);
      const y = Math.max(0, banda.y);
      const w = Math.min(trabajo.ancho, banda.x + banda.w) - x;
      const h = Math.min(trabajo.alto, banda.y + banda.h) - y;
      if (w > 20 && h > 10) {
        const recorteNumero = await ad.imagen.recortar(trabajo, { x, y, w, h });
        const ampliada = await ad.imagen.escalarAAncho(recorteNumero, Math.min(1600, recorteNumero.ancho * 3));
        const f = ampliada.ancho / recorteNumero.ancho;
        const { palabras } = await ad.ocr.leer(ampliada, "0123456789.");
        for (const hz of hallazgosPorValorEsperado(palabras, [{ campo: "numeroDocumento", valor: numeroEsperado }])) {
          // Se marca solo si coincide: una lectura con dígitos distintos no es el número probado.
          const digitos = (v: string) => v.replace(/\D/g, "").replace(/^0+/, "");
          if (!hz.valor || !hz.caja || digitos(hz.valor) !== digitos(numeroEsperado)) continue;
          hNumero.push({ ...hz, caja: { x: x + hz.caja.x / f, y: y + hz.caja.y / f, w: hz.caja.w / f, h: hz.caja.h / f } });
        }
      }
    }
    if (hNumero.length) diagnostico.push("número de documento impreso ubicado por el OCR de su zona");
  }

  const hallazgos = [...hCodigo, ...hImpreso, ...hCorroborado, ...hNumero, ...hMrz];

  // Qué cara es, por lo que se vio y no por el paso en que llegó la foto.
  const contenido = codigo?.texto?.trim() ?? "";
  const declaraEnCara = (c: Cara, tipo: "pdf417" | "qr") =>
    !!contenido &&
    tipoDelCodigo === tipo &&
    catalogo.some((m) => m.codigos2d.some((d) => d.cara === c && d.tipo === tipo && (!d.forma || d.forma.test(contenido))));
  // Las señales salen de la foto ENTERA (lo que se miró para identificar el modelo, antes de
  // recortar) y del recorte: en una foto con las dos caras sin separar, el recorte se queda con una
  // tarjeta y perdería las señales de la otra (revisión del recorrido 3).
  const senales = {
    codigoDelFrente: declaraEnCara("frente", "pdf417"),
    codigoDelDorso: declaraEnCara("dorso", "pdf417"),
    qrDelDorso: declaraEnCara("dorso", "qr"),
    mrz: !!mrz || !!mrzIdent,
    rotulosDelFrente: Math.max(hallazgosDelFrente(impreso.palabras).length, hallazgosDelFrente(vistaIdent.palabras).length),
    rotulosDelDorso: Math.max(hallazgosDelDorso(impreso.palabras).length, hallazgosDelDorso(vistaIdent.palabras).length),
  };
  const caraReconocida = caraPorSenales(senales);
  diagnostico.push(`cara reconocida: ${caraReconocida ?? "ninguna"} ${JSON.stringify(senales)}`);

  // El código 2D lleva adentro el número de trámite, así que su lugar en la
  // foto es tan sensible como el número impreso. Las esquinas vienen en
  // coordenadas de ANTES del recorte: se corren al origen del recorte.
  const zonasSensibles: Rectangulo[] = [];
  if (codigo?.esquinas && hCodigo.some((h) => h.campo === "numeroTramite")) {
    const xs = codigo.esquinas.map((p) => p.x);
    const ys = codigo.esquinas.map((p) => p.y);
    const margen = Math.max(4, (Math.max(...xs) - Math.min(...xs)) * 0.04);
    zonasSensibles.push({
      x: Math.min(...xs) - (recorte?.x ?? 0) - margen,
      y: Math.min(...ys) - (recorte?.y ?? 0) - margen,
      w: Math.max(...xs) - Math.min(...xs) + margen * 2,
      h: Math.max(...ys) - Math.min(...ys) + margen * 2,
    });
  }
  // El Nº de Trámite impreso: se sabe qué número es (lo dio el código) y se lo
  // busca en su banda de la tarjeta, ampliada y con solo dígitos.
  let tramiteEnLaImagen: Rectangulo | null = null;
  const tramite = hCodigo.find((h) => h.campo === "numeroTramite")?.valor;
  const zonaTramite = modelo?.zonas.find((z) => z.cara === cara && z.campo === "numeroTramite" && z.sensible);
  if (tramite && zonaTramite && tarjetaEnLaImagen) {
    const z = zonaTramite.caja;
    const banda = cajaEnLaImagen({ x: z.x - 0.06, y: z.y - 0.03, w: z.w + 0.08, h: z.h + 0.05 }, tarjetaEnLaImagen);
    if (banda) {
      const x = Math.max(0, banda.x);
      const y = Math.max(0, banda.y);
      const w = Math.min(trabajo.ancho, banda.x + banda.w) - x;
      const h = Math.min(trabajo.alto, banda.y + banda.h) - y;
      if (w > 20 && h > 10) {
        const recorteBanda = await ad.imagen.recortar(trabajo, { x, y, w, h });
        const ampliada = await ad.imagen.escalarAAncho(recorteBanda, Math.min(1600, recorteBanda.ancho * 3));
        const f = ampliada.ancho / recorteBanda.ancho;
        const { palabras } = await ad.ocr.leer(ampliada, "0123456789");
        const palabra = palabraDelTramite(palabras, tramite);
        if (palabra) {
          const c = cajaDelTramiteEntero(palabra, tramite);
          tramiteEnLaImagen = recortarA({ x: x + c.x / f, y: y + c.y / f, w: c.w / f, h: c.h / f }, { x, y, w, h });
        }
      }
    }
    diagnostico.push(tramiteEnLaImagen ? "trámite impreso ubicado por el OCR de su banda" : "trámite impreso no ubicado: se tapa por la plantilla");
  }

  diagnostico.push(
    `fuentes: código ${hCodigo.length ? "sí" : "no"} · impreso ${hImpreso.length}${
      hCorroborado.length ? `+${hCorroborado.length} ubicados` : ""
    } · MRZ ${hMrz.length ? "sí" : "no"}`
  );

  return {
    imagen: trabajo,
    zonasSensibles,
    renglonesDelImpreso: filas(impreso.palabras).map((f) =>
      f.map((w) => w.text.trim()).filter(Boolean).join(" | ")
    ),
    transformacion: { giroGrados, recorte, origenDelGiro, origenDeLaTarjeta },
    campos: consolidar(hallazgos),
    hallazgos,
    modelo,
    confianzaModelo: identificacion.confianza,
    inclinacionGrados,
    tarjetaUbicada: !!tarjeta,
    tarjetaEnLaImagen,
    tarjetaPorAncla,
    caraReconocida,
    tramiteEnLaImagen,
    pxPorMm,
    diagnostico,
  };
}
